import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useSeo from '../hooks/useSeo';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft, ArrowRight, BookOpen, Clock, ChevronRight, Lightbulb,
  AlertTriangle, ShieldCheck, Quote, Users, Sparkles, GraduationCap,
  CheckCircle2, BookmarkPlus, ListTree, Newspaper,
} from 'lucide-react';
import EtmFinalQuiz from '../components/EtmFinalQuiz'; // eslint-disable-line no-unused-vars -- conservé pour rollback du pivot magazine 2026
import { SafeVideoEmbed } from '../components/SafeVideoEmbed';
import { ETM_ISSUES_INDEX, getEtmIssueBySlug } from '../data/etmIssues';
import { getSatellitesByParent } from '../data/etmIssues/satellites';
import { getEnqueteBySlug, ENQUETES_INDEX } from '../data/magazinesEnquetes';
import api from '../api';
import { useApp } from '../contexts/AppContext';

// Mapping type de section → icône par défaut (peut être surchargé par data.icon)
const SECTION_TYPE_META = {
  lesson:     { icon: BookOpen,     accent: 'hsl(var(--primary))', bg: 'rgba(207,32,49,0.05)' },
  didyouknow: { icon: Lightbulb,    accent: '#0891b2',             bg: 'rgba(8,145,178,0.06)' },
  trap:       { icon: AlertTriangle,accent: '#f59e0b',             bg: 'rgba(245,158,11,0.07)' },
  tip:        { icon: Quote,        accent: '#7c3aed',             bg: 'rgba(124,58,237,0.06)' },
  story:      { icon: Users,        accent: '#16a34a',             bg: 'rgba(22,163,74,0.06)' },
};

function SectionBlock({ section, index }) {
  const meta = SECTION_TYPE_META[section.type] || SECTION_TYPE_META.lesson;
  const Icon = section.icon || meta.icon;

  // Layout différent selon le type
  if (section.type === 'didyouknow' || section.type === 'trap' || section.type === 'tip' || section.type === 'story') {
    return (
      <aside
        className="rounded-xl border-l-4 my-6 sm:my-8 p-4 sm:p-5"
        style={{ borderColor: meta.accent, background: meta.bg }}
        data-testid={`section-${section.type}-${index}`}
      >
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-4 h-4" style={{ color: meta.accent }} />
          <span
            className="text-[11px] font-black uppercase tracking-[0.15em]"
            style={{ color: meta.accent }}
          >
            {section.title}
          </span>
        </div>
        <div
          className="text-sm sm:text-base leading-relaxed text-foreground/90"
          dangerouslySetInnerHTML={{ __html: section.body }}
        />
      </aside>
    );
  }

  // Default: lesson (chapitre principal)
  return (
    <section className="my-7 sm:my-10" data-testid={`section-lesson-${index}`}>
      <div className="mb-3 sm:mb-4">
        {section.partLabel && (
          <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-primary mb-1">
            {section.partLabel}
          </div>
        )}
        <h2
          className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight flex items-start gap-2"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          <Icon className="w-6 h-6 mt-1 text-primary flex-shrink-0" />
          <span>{section.title}</span>
        </h2>
      </div>

      <div className="space-y-3 text-[15px] sm:text-base leading-relaxed text-foreground/90">
        {section.paragraphs.map((p, i) => (
          <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
        ))}
      </div>

      {/* Vidéo YouTube RGPD-safe (miniature + play → iframe nocookie) */}
      {section.videoEmbed && (
        <figure
          className="my-5"
          data-testid={`section-video-${section.videoEmbed.testid || index}`}
        >
          <SafeVideoEmbed
            videoId={section.videoEmbed.videoId}
            listId={section.videoEmbed.listId}
            title={section.videoEmbed.title || 'Vidéo'}
            channel={section.videoEmbed.channel || 'YouTube'}
            aspect={section.videoEmbed.aspect || '16/9'}
            testid={`enquete-video-${section.videoEmbed.testid || index}`}
          />
          {(section.videoEmbed.caption || section.videoEmbed.credit) && (
            <figcaption className="mt-2 text-[11px] text-muted-foreground flex flex-wrap items-center gap-2 justify-between px-1">
              {section.videoEmbed.caption && (
                <span
                  className="italic"
                  dangerouslySetInnerHTML={{ __html: section.videoEmbed.caption }}
                />
              )}
              {section.videoEmbed.credit && (
                <span className="text-[10px] uppercase tracking-wider font-semibold opacity-75">
                  © {section.videoEmbed.credit}
                </span>
              )}
            </figcaption>
          )}
        </figure>
      )}

      {section.keyPoints && section.keyPoints.length > 0 && (
        <div className="mt-4 rounded-lg border bg-card/40 p-3 sm:p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            À retenir
          </div>
          <ul className="space-y-1.5">
            {section.keyPoints.map((kp, i) => (
              <li key={i} className="text-sm flex gap-2">
                <span className="text-emerald-600 mt-0.5 flex-shrink-0">›</span>
                <span dangerouslySetInnerHTML={{ __html: kp }} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export default function EtmIssuePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  // Résolution dans 2 registries : Magazines ETM officiels OU Enquêtes & Témoignages
  const issue = useMemo(() => getEtmIssueBySlug(slug) || getEnqueteBySlug(slug), [slug]);
  const satellites = useMemo(() => getSatellitesByParent(slug), [slug]);
  const [quizVisible, setQuizVisible] = useState(false);
  const quizRef = useRef(null);
  // Identifie l'origine pour les URLs de navigation entre numéros / fallback "bientôt publié"
  const etmEntry = ETM_ISSUES_INDEX.find((i) => i.slug === slug);
  const enqueteEntry = ENQUETES_INDEX.find((e) => e.slug === slug);
  const indexEntry = etmEntry || enqueteEntry;
  const isEnquete = !!enqueteEntry || issue?.series === 'enquetes';
  const baseUrl = isEnquete ? '/enquete' : '/apprendre';

  // Tracking : marquer le numéro comme "lu" au montage (silencieux)
  useEffect(() => {
    if (!user || !issue) return;
    api.markEtmRead(slug).catch(() => { /* tracking silent */ });
  }, [user, issue, slug]);

  useSeo({
    title: issue ? `${issue.title} — ${isEnquete ? 'Enquête' : 'Numéro'} ${issue.issueNumber} ${isEnquete ? '' : 'du Magazine pédagogique'}` : 'Magazine pédagogique — Code 2 Roues',
    description: issue?.seoDescription || "Magazine pédagogique pour comprendre le code moto en profondeur — études de cas, statistiques, témoignages.",
    path: `${baseUrl}/${slug}`,
    keywords: issue?.seoKeywords || '',
  });

  // Numéro en cours mais non publié → page d'attente
  if (!issue && indexEntry && indexEntry.status === 'coming-soon') {
    const seriesLabel = isEnquete ? `Enquête ${indexEntry.number}/8` : `Numéro ${indexEntry.number}/9`;
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center space-y-4">
          <Badge variant="outline" className="text-[10px]">{seriesLabel} · Bientôt publié</Badge>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {indexEntry.title}
          </h1>
          <p className="text-muted-foreground">{indexEntry.teaser}</p>
          <p className="text-sm text-muted-foreground">
            Cet épisode est en cours de rédaction. En attendant, découvre les autres numéros publiés.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 pt-3">
            {isEnquete ? (
              <Button onClick={() => navigate('/enquete/ados-mobylette')} data-testid="goto-enquete-pilot">
                Lire l'enquête n°1 (Ados à mobylette)
              </Button>
            ) : (
              <Button onClick={() => navigate('/apprendre/epi')} data-testid="goto-epi-issue">
                Lire le numéro 7 (EPI)
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-home">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Slug invalide
  if (!issue) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center space-y-4">
          <h1 className="text-2xl font-bold">Numéro introuvable</h1>
          <p className="text-muted-foreground">Ce numéro n'existe pas. Découvre les 9 numéros du Magazine pédagogique.</p>
          <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
        </div>
      </div>
    );
  }

  const handleStartQuiz = () => {
    setQuizVisible(true);
    setTimeout(() => {
      quizRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <article className="min-h-screen bg-background pb-16" data-testid={`etm-issue-${issue.slug}`}>
      {/* COUVERTURE */}
      <header
        className="relative overflow-hidden border-b"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 100% 0%, rgba(207,32,49,0.10), transparent 60%), radial-gradient(ellipse 50% 40% at 0% 100%, rgba(8,145,178,0.06), transparent 60%), hsl(var(--card))',
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(-45deg, transparent 0 14px, hsl(var(--foreground)) 14px 15px)',
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 lg:py-14">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-foreground mb-4 sm:mb-5"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6 lg:gap-8 items-center">
            <div className="space-y-3 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-primary text-primary-foreground border-none text-[10px]">
                  Numéro {issue.issueNumber}/{issue.totalIssues}
                </Badge>
                <span className="text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold text-primary">
                  {issue.kicker}
                </span>
              </div>
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                data-testid="issue-title"
              >
                {issue.title}
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-foreground/70 leading-relaxed max-w-2xl">
                {issue.subtitle}
              </p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] sm:text-xs text-muted-foreground pt-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-background/60">
                  <Clock className="w-3.5 h-3.5" /> {issue.readTimeMin} min de lecture
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-background/60">
                  <ListTree className="w-3.5 h-3.5" /> {issue.sections.length} chapitres + encadrés
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border bg-background/60">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" /> 100 % gratuit
                </span>
              </div>
            </div>

            {/* Image héro */}
            <div className="rounded-xl overflow-hidden aspect-[4/3] sm:aspect-[3/2] lg:aspect-square border shadow-lg max-w-md w-full mx-auto lg:max-w-none">
              <img
                src={issue.heroImage}
                alt={issue.heroImageAlt}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </header>

      {/* CORPS */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        {/* ÉDITO */}
        <section className="rounded-xl border bg-card/50 p-4 sm:p-5 mb-2" data-testid="issue-edito">
          <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-primary mb-2">
            Édito
          </div>
          <h2
            className="text-xl sm:text-2xl font-bold mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {issue.edito.title}
          </h2>
          <p
            className="text-sm sm:text-base leading-relaxed text-foreground/85"
            dangerouslySetInnerHTML={{ __html: issue.edito.body }}
          />
          <div className="text-xs italic text-muted-foreground mt-3">— {issue.edito.signedBy}</div>
        </section>

        {/* SECTIONS PÉDAGOGIQUES */}
        {issue.sections.map((s, i) => (
          <SectionBlock key={i} section={s} index={i} />
        ))}

        {/* CHIFFRE MARQUANT (avant glossaire) */}
        {issue.keyStat && (
          <aside
            className="my-8 sm:my-10 rounded-xl p-6 sm:p-8 text-center relative overflow-hidden"
            style={{
              background: `radial-gradient(ellipse at center, ${issue.keyStat.accent}22, transparent 70%)`,
              border: `1px solid ${issue.keyStat.accent}44`,
            }}
            data-testid="issue-key-stat"
          >
            <div
              className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] mb-2"
              style={{ color: issue.keyStat.accent }}
            >
              {issue.keyStat.kicker}
            </div>
            <div
              className="text-5xl sm:text-7xl font-black tabular-nums leading-none"
              style={{ color: issue.keyStat.accent }}
            >
              {issue.keyStat.big}
            </div>
            <div className="text-sm sm:text-base font-bold mt-2">{issue.keyStat.middle}</div>
            <div className="text-xs sm:text-sm text-foreground/70 mt-1 max-w-sm mx-auto">
              {issue.keyStat.label}
            </div>
            <div className="text-[10px] text-muted-foreground italic mt-3">
              Source : {issue.keyStat.source}
            </div>
          </aside>
        )}

        {/* ENCADRÉ "CÔTÉ FORMATEUR ECSR" — uniquement sur les Enquêtes */}
        {issue.ecsrCorner && (
          <aside
            className="my-8 sm:my-10 rounded-xl border-2 border-dashed border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 p-5 sm:p-6"
            data-testid="issue-ecsr-corner"
          >
            <div className="flex items-center gap-2 mb-3">
              <GraduationCap className="w-4 h-4 text-[hsl(var(--primary))]" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                {issue.ecsrCorner.title}
              </span>
            </div>
            <p className="text-sm sm:text-base leading-relaxed text-foreground/85 mb-4">
              {issue.ecsrCorner.intro}
            </p>
            <ul className="space-y-2 mb-4">
              {issue.ecsrCorner.points.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                  <CheckCircle2 className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            {issue.ecsrCorner.seeCasLink && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(issue.ecsrCorner.seeCasLink.to)}
                data-testid={issue.ecsrCorner.seeCasLink.testid || 'issue-ecsr-cas-link'}
                className="gap-2"
              >
                <BookOpen className="w-4 h-4" />
                {issue.ecsrCorner.seeCasLink.label}
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </aside>
        )}

        {/* GLOSSAIRE */}
        {issue.glossary && issue.glossary.length > 0 && (
          <section className="my-8" data-testid="issue-glossary">
            <div className="text-[11px] font-black uppercase tracking-[0.18em] text-primary mb-2 flex items-center gap-1.5">
              <BookmarkPlus className="w-3.5 h-3.5" />
              Glossaire — les termes à retenir
            </div>
            <h2
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Le vocabulaire technique
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {issue.glossary.map((g, i) => (
                <div key={i} className="rounded-md border bg-card/40 p-3">
                  <dt className="font-bold text-sm font-mono">
                    <code className="bg-primary/10 text-primary px-1.5 py-0.5 rounded">{g.term}</code>
                  </dt>
                  <dd className="text-xs text-foreground/75 mt-1.5 leading-relaxed">{g.def}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* Pivot magazine 2026 : la section QCM final + le CTA ont été supprimés
            pour aller vers un format purement éditorial. Le passage au numéro
            suivant se fait désormais via le bloc "Pour aller plus loin". */}

        {/* ARTICLES SATELLITES — densification du dossier (uniquement ETM, pas Enquêtes) */}
        {!isEnquete && satellites.length > 0 && (
          <section className="my-10 sm:my-12" data-testid="issue-satellites">
            <div className="flex items-center gap-2 mb-3">
              <Newspaper className="w-4 h-4 text-[hsl(var(--primary))]" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                Aller plus loin · Dossier {issue.title}
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight mb-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Les articles du dossier
            </h2>
            <p className="text-sm sm:text-base text-foreground/70 leading-relaxed mb-5 max-w-2xl">
              Huit articles courts pour creuser chaque facette de ce numéro — à lire à votre rythme.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {satellites.map((sat) => {
                const isPublished = sat.status === 'published';
                const CardWrapper = isPublished ? Link : 'div';
                const wrapperProps = isPublished
                  ? { to: `/apprendre/${slug}/${sat.slug}`, className: 'group block' }
                  : { className: 'block opacity-70 cursor-default' };
                return (
                  <CardWrapper
                    key={sat.slug}
                    {...wrapperProps}
                    data-testid={`satellite-card-${sat.slug}`}
                  >
                    <Card
                      className={`h-full transition-all ${
                        isPublished
                          ? 'hover:border-[hsl(var(--primary))]/60 hover:shadow-md'
                          : 'border-dashed'
                      }`}
                    >
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                            {sat.kicker}
                          </div>
                          {!isPublished && (
                            <Badge variant="outline" className="text-[9px] font-bold uppercase tracking-wider">
                              Bientôt
                            </Badge>
                          )}
                        </div>
                        <h3
                          className={`text-base sm:text-lg font-black leading-tight mb-2 transition-colors ${
                            isPublished ? 'group-hover:text-[hsl(var(--primary))]' : ''
                          }`}
                          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                        >
                          {sat.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed mb-3 line-clamp-3">
                          {sat.teaser}
                        </p>
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {sat.readTimeMin} min
                          </span>
                          {isPublished && (
                            <span className="inline-flex items-center gap-1 font-bold text-[hsl(var(--primary))] group-hover:gap-1.5 transition-all">
                              Lire <ArrowRight className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </CardWrapper>
                );
              })}
            </div>
          </section>
        )}

        {/* NEXT ISSUE */}
        {issue.nextIssue && (
          <Card
            className="mt-8 cursor-pointer hover:shadow-md transition-all group"
            onClick={() => {
              const nextBase = issue.nextIssue.series === 'enquetes' ? '/enquete' : '/apprendre';
              navigate(`${nextBase}/${issue.nextIssue.slug}`);
            }}
            data-testid="issue-next-card"
          >
            <CardContent className="p-4 sm:p-5 flex items-center gap-4">
              <div className="text-3xl sm:text-4xl font-extrabold text-primary/30 tabular-nums">
                N°{issue.nextIssue.number}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Prochain numéro suggéré</div>
                <div className="font-bold text-sm sm:text-base truncate">{issue.nextIssue.title}</div>
                <div className="text-xs text-muted-foreground">{issue.nextIssue.short}</div>
              </div>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform text-muted-foreground" />
            </CardContent>
          </Card>
        )}

        {/* RETOUR PROGRAMME */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>Programme pédagogique ETM · {issue.issueNumber}/9 numéros</span>
          <Link to="/" className="hover:text-foreground inline-flex items-center gap-1" data-testid="back-program-link">
            Voir tout le programme <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </main>
    </article>
  );
}
